import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, ShoppingBag, Settings, GraduationCap, Landmark, ArrowRight, CheckCircle } from "lucide-react";

const INDUSTRIES = [
  {
    id: "enterprise",
    Icon: Building2,
    title: "Enterprise Corporate Offices",
    subtitle: "End-to-end IT infrastructure for large corporates",
    color: "#0F62FE", bg: "#EFF6FF",
    desc: "From HDMI conference room cables to structured Cat6 LAN networks, we supply enterprise-grade hardware for large corporate offices needing reliable, scalable infrastructure.",
    products: ["HDMI & DisplayPort Cables","Cat6 LAN & Patch Panels","PoE & Managed Switches","CCTV Surveillance Systems","ATEN KVM Switches","Quantron Peripherals"],
    benefits: ["Bulk order fulfillment within 48 hours","Dedicated account manager","Site visit & hardware consultation","Post-delivery support"]
  },
  {
    id: "itdealers",
    Icon: ShoppingBag,
    title: "IT Dealers & Channel Resellers",
    subtitle: "High-margin distributor programmes for resellers",
    color: "#7C3AED", bg: "#F5F3FF",
    desc: "We offer channel resellers and IT dealers competitive distributor pricing, ready stock for fast resale, and an extensive range of 2000+ SKUs across all major IT hardware categories.",
    products: ["USB & HDMI Cables (bulk rolls)","Quantron Keyboards & Mice","Power Cords & Adapters","Networking Consumables","Webcams & Headsets","SMPS & CCTV Accessories"],
    benefits: ["Distributor-tier pricing on bulk orders","No minimum order value restrictions","Fast credit terms for regular partners","Product training support"]
  },
  {
    id: "systemintegrators",
    Icon: Settings,
    title: "System Integrators",
    subtitle: "Project-based supply for SI firms",
    color: "#059669", bg: "#ECFDF5",
    desc: "System integrators rely on us for project-based procurement — from structured cabling and network switches to CCTV systems and KVM solutions — with consistent quality and on-time delivery.",
    products: ["Structured Cabling (Cat6 + Patch Panels)","PoE Switches & Routers","Hikvision CCTV + DVR Systems","ATEN KVM Over IP","Server Racks & PDUs","Fiber Media Converters"],
    benefits: ["Project-based supply planning","Batch delivery scheduling","Technical spec consultation","OEM support documentation"]
  },
  {
    id: "academia",
    Icon: GraduationCap,
    title: "Academics & Public Institutions",
    subtitle: "Cost-effective IT for educational bodies",
    color: "#D97706", bg: "#FFFBEB",
    desc: "Schools, colleges, and public institutions trust us for cost-effective IT peripherals, computer lab accessories, networking gear, and CCTV surveillance — with competitive GEM/tender pricing.",
    products: ["Computer Lab Accessories","USB Hubs & Docking Stations","Webcams & Headsets","Cat6 Campus Networking","IP Camera Systems","Thin Client Terminals"],
    benefits: ["GEM portal registered supplier","Competitive tender pricing","Bulk educational discounts","Long warranty support"]
  },
  {
    id: "government",
    Icon: Landmark,
    title: "Government Offices",
    subtitle: "Compliant IT supply for government departments",
    color: "#DC2626", bg: "#FEF2F2",
    desc: "We supply government departments and PSUs with quality-certified IT hardware, surveillance systems, and networking solutions — compliant with procurement regulations and with full documentation support.",
    products: ["CCTV Surveillance Networks","Structured LAN Cabling","Server Room Rack Solutions","Networking Switches & Routers","KVM Console Solutions","Power Management Systems"],
    benefits: ["GST-registered vendor","Full invoice & documentation","GeM empanelment support","Audit-ready supply records"]
  },
];

export default function Industries() {
  return (
    <div>
      {/* Hero */}
      <section className="bg-[#2D3748] py-14 px-6" data-testid="industries-hero">
        <div className="max-w-7xl mx-auto">
          <nav className="text-xs text-gray-500 mb-4">
            <Link to="/" className="hover:text-gray-300 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-300">Industries</span>
          </nav>
          <h1 className="font-heading text-4xl lg:text-5xl font-black text-white mb-3">Industries We Serve</h1>
          <p className="text-gray-400 text-base max-w-2xl leading-relaxed">
            From enterprise offices to government institutions, we deliver tailored IT infrastructure solutions across key industry verticals.
          </p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-14 bg-white" data-testid="industries-main">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-8">
            {INDUSTRIES.map(({ id, Icon, title, subtitle, color, bg, desc, products, benefits }, i) => (
              <motion.div
                key={id}
                data-testid={`industry-card-${id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="border border-gray-200 hover:border-[#0F62FE] transition-all duration-200 hover:shadow-lg bg-white overflow-hidden"
              >
                <div className="flex items-center gap-4 p-6 border-b border-gray-100" style={{ backgroundColor: bg }}>
                  <div className="p-3 bg-white shadow-sm shrink-0">
                    <Icon size={24} style={{ color }} />
                  </div>
                  <div>
                    <h2 className="font-heading font-black text-[#111827] text-lg leading-tight">{title}</h2>
                    <p className="text-sm mt-0.5" style={{ color }}>{subtitle}</p>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="md:col-span-1">
                      <p className="text-[#4B5563] text-sm leading-relaxed">{desc}</p>
                    </div>

                    <div>
                      <h4 className="font-heading font-bold text-[#111827] text-xs uppercase tracking-widest mb-3">Key Products Supplied</h4>
                      <ul className="space-y-2">
                        {products.map((p) => (
                          <li key={p} className="flex items-center gap-2 text-sm text-[#4B5563]">
                            <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                            {p}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-heading font-bold text-[#111827] text-xs uppercase tracking-widest mb-3">What We Offer</h4>
                      <ul className="space-y-2">
                        {benefits.map((b) => (
                          <li key={b} className="flex items-start gap-2 text-sm text-[#4B5563]">
                            <CheckCircle size={14} className="mt-0.5 shrink-0" style={{ color }} />
                            {b}
                          </li>
                        ))}
                      </ul>
                      <Link
                        to="/contact"
                        data-testid={`industry-cta-${id}`}
                        className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold transition-colors"
                        style={{ color }}
                      >
                        Get a Quote <ArrowRight size={12} />
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-[#F3F4F6]" data-testid="industries-cta">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl lg:text-3xl font-black text-[#111827] mb-3">Don't See Your Industry?</h2>
          <p className="text-[#4B5563] text-base mb-6 max-w-md mx-auto">
            We serve diverse sectors. Contact us to discuss your specific IT infrastructure requirements.
          </p>
          <Link to="/contact" data-testid="industries-contact-btn" className="inline-flex items-center gap-2 bg-[#0F62FE] hover:bg-[#0052CC] text-white font-semibold px-7 py-3.5 transition-all duration-200 hover:shadow-lg">
            Contact Us <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
